// Generic debounced auto-submit for sidebar search forms that target a Turbo
// Frame. Handles all forms with data-turbo-frame, including job-results
// (job_search.js owns sidebar/MyJobs/expand — not the form binding).

// Returns true while any TomSelect dropdown is open so search forms do not submit mid-selection.
function isAnyTomSelectOpen() {
  for (const el of document.querySelectorAll(
    "select.form-control, select.form-select",
  )) {
    if (el.tomselect?.isOpen) return true;
  }
  return false;
}

const controllers = new WeakMap();

// Adds debounced input/change submission to one Turbo Frame search form.
function bindForm(form) {
  if (controllers.has(form)) return;

  let timer;
  const controller = new AbortController();
  controllers.set(form, {
    controller,
    cancel: () => clearTimeout(timer),
  });

  // Delays submission and retries briefly while a TomSelect menu is active.
  function scheduleSubmit(delay) {
    clearTimeout(timer);
    timer = setTimeout(function attempt() {
      if (isAnyTomSelectOpen()) {
        timer = setTimeout(attempt, 150);
        return;
      }
      form.requestSubmit();
    }, delay);
  }

  // Exposed so job_search.js can cancel a pending debounce before a
  // My Jobs toggle direct submit.
  form._cancelDebounce = () => clearTimeout(timer);

  form.addEventListener(
    "change",
    (e) => {
      if (
        !e.target.matches(
          "select, input[type='date'], input[type='checkbox'], input[type='radio']",
        )
      )
        return;
      scheduleSubmit(400);
    },
    { signal: controller.signal },
  );

  form.addEventListener(
    "input",
    (e) => {
      if (!e.target.matches("input[type='text']")) return;
      const hasValue = e.target.value.trim().length > 0;
      e.target.classList.toggle("border", hasValue);
      e.target.classList.toggle("border-success", hasValue);
      scheduleSubmit(700);
    },
    { signal: controller.signal },
  );

  // Cancel any pending debounce when the form is submitted explicitly
  // (e.g. Search button click). Prevents a double frame request when
  // the manual submit races the 700ms auto-search timer.
  form.addEventListener("submit", () => clearTimeout(timer), {
    signal: controller.signal,
  });
}

// Forms targeting _top are action/mutation forms (mark-as-read, delete, etc.),
// not search forms — skip them.
// Finds all frame-targeted search forms on the current page or newly-rendered frame.
function bindAll() {
  document.querySelectorAll("form[data-turbo-frame]").forEach((form) => {
    if (form.dataset.turboFrame !== "_top") bindForm(form);
  });
}

// Re-bind after full visits, frame visits, and stream updates because Turbo can replace form DOM.
document.addEventListener("turbo:load", bindAll);
document.addEventListener("turbo:frame-load", bindAll);
document.addEventListener("turbo:before-stream-render", () => {
  setTimeout(bindAll, 0);
});

// Abort listeners and pending timers before Turbo snapshots the page.
document.addEventListener("turbo:before-cache", () => {
  document.querySelectorAll("form[data-turbo-frame]").forEach((form) => {
    if (form.dataset.turboFrame === "_top") return;
    const state = controllers.get(form);
    if (!state) return;
    state.cancel();
    state.controller.abort();
    controllers.delete(form);
  });
});
